import { KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { Check, ChevronDown, CornerDownRight } from "lucide-react";
import { useSelector } from "react-redux";
import { COLORS } from "constants/variables";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import CustomText from "components/Atoms/CustomText/CustomText";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";
import { ScrollCardsMenuProps } from "./ScrollCardsMenuModel";
import { Card, CardsContainer, MobileMenuButton, MobileMenuItem, MobileMenuList, MobileMenuPanel, MobileMenuWrapper, SectionContext, SectionNavigation } from "./ScrollCardsMenuStyled";

const ScrollCardsMenu = ({ cards, sectionTitle }: ScrollCardsMenuProps) => {
  const [selectedId, setSelectedId] = useState(cards[0]?.id);
  const [isOpen, setIsOpen] = useState(false);
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const sectionIds = cards.map(card => card.id).join(",");
  const selectedCard = cards.find(card => card.id === selectedId) || cards[0];

  useEffect(() => {
    if (!isOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!navRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus({ preventScroll: true });
      }
    };
    const closeOnResize = () => setIsOpen(false);
    const current = menuRef.current?.querySelector<HTMLButtonElement>("[aria-current='location']");
    current?.focus({ preventScroll: true });
    if (current && menuRef.current) {
      menuRef.current.scrollTop = current.offsetTop - menuRef.current.offsetTop - menuRef.current.clientHeight / 2;
    }
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", closeOnResize);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", closeOnResize);
    };
  }, [isOpen]);

  useEffect(() => {
    const ids = sectionIds.split(",");
    let frame = 0;
    const updateSection = () => {
      const headerBottom = navRef.current?.closest("[data-site-header]")?.getBoundingClientRect().bottom || 0;
      const sections = ids.map(id => document.getElementById(id)).filter(Boolean);
      let current = ids[0];
      for (const section of sections) {
        if (section.getBoundingClientRect().top <= headerBottom + 32) current = section.id;
      }
      setSelectedId(current);
    };
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(updateSection);
    };
    updateSection();
    window.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleScroll);
    };
  }, [sectionIds]);

  useEffect(() => {
    const list = listRef.current;
    const active = list?.querySelector<HTMLButtonElement>("[aria-current='location']");
    if (!list || !active) return;
    const listBounds = list.getBoundingClientRect();
    const activeBounds = active.getBoundingClientRect();
    if (activeBounds.left < listBounds.left || activeBounds.right > listBounds.right) {
      list.scrollTo({ left: list.scrollLeft + activeBounds.left - listBounds.left, behavior: "auto" });
    }
  }, [selectedId]);

  const handleScroll = (id: string) => {
    setSelectedId(id);
    setIsOpen(false);
    if (isOpen) triggerRef.current?.focus({ preventScroll: true });
    document.getElementById(id)?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
  };

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const buttons = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("button") || []);
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    let nextIndex = index;
    if (event.key === "ArrowDown") nextIndex = (index + 1) % buttons.length;
    else if (event.key === "ArrowUp") nextIndex = (index - 1 + buttons.length) % buttons.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = buttons.length - 1;
    else return;
    event.preventDefault();
    buttons[nextIndex]?.focus();
  };

  return (
    <SectionNavigation ref={navRef} aria-label={sectionTitle}>
      <SectionContext>
        <CornerDownRight size={16} aria-hidden="true" />
        <span>{sectionTitle}</span>
      </SectionContext>
      <CardsContainer ref={listRef}>
        {cards.map(card => (
          <Card key={card.id} type="button" $active={selectedId === card.id} $biggerCursor={biggerCursor}
            aria-current={selectedId === card.id ? "location" : undefined}
            aria-controls={card.id} onClick={() => handleScroll(card.id)}>
            <CustomText type={TextType.P2} text={card.title}
              color={selectedId === card.id ? COLORS.Yellow : COLORS.AbsoluteWhite} />
          </Card>
        ))}
      </CardsContainer>
      <MobileMenuWrapper>
        <MobileMenuButton ref={triggerRef} type="button" $biggerCursor={biggerCursor} $open={isOpen}
          aria-expanded={isOpen} aria-controls={menuId} onClick={() => setIsOpen(open => !open)}>
          <CustomText type={TextType.P2} text={selectedCard?.title} color={COLORS.AbsoluteWhite} />
          <ChevronDown size={18} aria-hidden="true" />
        </MobileMenuButton>
      </MobileMenuWrapper>
      {isOpen && (
        <MobileMenuPanel id={menuId} onBlur={event => {
          if (!event.currentTarget.contains(event.relatedTarget as Node) && event.relatedTarget !== triggerRef.current) setIsOpen(false);
        }}>
          <MobileMenuList ref={menuRef} onKeyDown={handleMenuKeyDown}>
            {cards.map(card => (
              <li key={card.id}>
                <MobileMenuItem type="button" $active={selectedId === card.id} $biggerCursor={biggerCursor}
                  aria-current={selectedId === card.id ? "location" : undefined}
                  aria-controls={card.id} onClick={() => handleScroll(card.id)}>
                  <CustomText type={TextType.P2} text={card.title}
                    color={selectedId === card.id ? COLORS.Yellow : COLORS.AbsoluteWhite} />
                  {selectedId === card.id && <Check size={18} aria-hidden="true" />}
                </MobileMenuItem>
              </li>
            ))}
          </MobileMenuList>
        </MobileMenuPanel>
      )}
    </SectionNavigation>
  );
};

export default ScrollCardsMenu;
